import { Box, Flex, Skeleton } from '@chakra-ui/react';
import {
  Button,
  Slider,
  SliderContainer,
  SliderItem,
  SliderTrigger,
} from '@diamond/shared/ui';
import { trackEvent } from '@diamond/shared/utils';
import { TRACKING_EVENTS, TRACKING_PAGES } from '@diamond/sol-buyer/constant';
import { useGetProductCategory } from '@diamond/sol-buyer-context';
import { ChevronLeftOutlined, ChevronRightOutlined } from '@mui/icons-material';
import { useRouter } from 'next/router';

// Or, if the file does not exist, create it at the specified path and export TRACKING_EVENTS from it.
import { Image } from '../common';

type CategorySliderProps = {
  size?: number;
  page?: number;
};

export function CategorySlider({ size = 100, page = 1 }: CategorySliderProps) {
  const {
    data: productCategory,
    isPending: isLoadingProductCategory,
    error: errorGetProductCategory,
  } = useGetProductCategory(size, page);
  const { query, push } = useRouter();

  function handleClickCategory(cat1?: string, catName?: string) {
    trackEvent(TRACKING_EVENTS.SELECT_CATEGORY, {
      category_id: cat1 ?? 'all',
      category_name: catName ?? 'all',
      section_name: 'Categories',
      page_name: TRACKING_PAGES.BERANDA,
    });
    const url = {
      pathname: '/product',
      query: { ...query, cat1: cat1, page: 1 },
    };
    push(url, undefined, { shallow: true });
  }

  const productCategoryData = productCategory ? productCategory : [];

  const items = [
    {
      icon: 'https://diamond-production-file-uploads.s3.ap-southeast-1.amazonaws.com/category/icons/ALL.svg',
      name: 'Semua Kategori',
    },
    ...productCategoryData.map((val) => {
      const isOverTitle = val.name.length > 20;
      const title = isOverTitle ? `${val.name.slice(0, 20)} ...` : val.name;
      return {
        id: val.id,
        icon: val.icon,
        name: title,
      };
    }),
  ];

  if (isLoadingProductCategory) {
    return (
      <Skeleton>
        <Flex h="74px" w="full" data-testid="CategorySliderSkeleton" />
      </Skeleton>
    );
  }

  if (errorGetProductCategory || !productCategory) {
    return null;
  }

  return (
    <Slider>
      <Flex alignItems="center" gap="3">
        <SliderTrigger
          direction="left"
          aria-label="back-icon"
          name="back-icon"
          icon={<ChevronLeftOutlined fontSize="medium" />}
          variant="solid"
          bgColor="navy"
          color="white"
          size="xs"
        />{' '}
        <SliderContainer pb="8px">
          {items.map((item) => (
            <SliderItem mr={2} key={item.name}>
              <Button
                border="1px"
                h="50px"
                w="full"
                leftIcon={
                  <Box position="relative" boxSize={{ base: 6, md: 8 }}>
                    <Image
                      src={item.icon}
                      alt="semua-kategori"
                      fill
                      style={{ objectFit: 'contain' }}
                    />
                  </Box>
                }
                onClick={() => {
                  if ('id' in item) {
                    return handleClickCategory(item.id, item.name);
                  }
                  return handleClickCategory();
                }}
                textTransform="capitalize"
                fontSize="md"
              >
                {item.name.toLowerCase()}
              </Button>
            </SliderItem>
          ))}
        </SliderContainer>
        <SliderTrigger
          direction="right"
          aria-label="next-icon"
          name="next-icon"
          icon={<ChevronRightOutlined fontSize="medium" />}
          variant="solid"
          bgColor="navy"
          color="white"
          size="xs"
        />
      </Flex>
    </Slider>
  );
}
